/**
 * SurveyLineItem Component
 * Inline row showing survey collection status within group session lists.
 * Three visual states: collecting (purple bg), future (dimmed), complete (normal).
 */

import { useState, useEffect } from 'react';
import { Group, Text, Badge, Button, Stack } from '@mantine/core';
import { FileText } from 'react-feather';
import { feedbackSurveysApi, type SurveyLineItemStatus } from '../../../../services/api/feedbackSurveys';
import { formatDate } from '../../../../utils/formatters';

interface SurveyLineItemProps {
  cohortId: string;
  groupId: string;
  surveyType: 'midpoint' | 'endpoint';
  colSpan: number;
  onReview: () => void;
  onReviewCoaches?: () => void;
  /** Label for the coach button — defaults to "Coaches" (CM view), use "My Feedback" for coach view */
  coachButtonLabel?: string;
}

const SURVEY_LABELS: Record<string, string> = {
  midpoint: 'Midpoint Survey',
  endpoint: 'Endpoint Survey',
};

export function SurveyLineItem({
  cohortId,
  groupId,
  surveyType,
  onReview,
  onReviewCoaches,
  coachButtonLabel = 'Coaches',
}: SurveyLineItemProps) {
  const [status, setStatus] = useState<SurveyLineItemStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    feedbackSurveysApi.getLineItemStatus(cohortId, groupId, surveyType).then((data) => {
      if (!cancelled) setStatus(data);
    }).catch(() => {
      // Silently fail
    });
    return () => { cancelled = true; };
  }, [cohortId, groupId, surveyType]);

  if (!status || status.state === 'not_configured') return null;

  const label = SURVEY_LABELS[surveyType] || surveyType;

  if (status.state === 'collecting') {
    return (
      <Group
        justify="space-between"
        align="center"
        wrap="nowrap"
        style={{
          backgroundColor: '#F4EEFF',
          borderLeft: '3px solid #7C3AED',
          padding: '16px 20px',
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        }}
        className="med-survey-line"
      >
        <Group gap="sm" align="center" className="med-survey-left">
          <FileText size={14} color="#7C3AED" />
          <Text size="sm" fw={600} c="#7C3AED">
            {label}
          </Text>
          <Badge size="sm" color="green" variant="light">
            Collecting
          </Badge>
        </Group>
        <Group gap="md" align="center" className="med-survey-right">
          {status.avg_satisfaction !== null && (
            <Stack gap={0} align="center" className="med-survey-avg">
              <Text size="sm" fw={600} c="#7C3AED">
                {status.avg_satisfaction.toFixed(1)}
              </Text>
              <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
                Avg Satisfaction
              </Text>
            </Stack>
          )}
          <Stack gap={0} align="center" className="med-survey-responses">
            <Text size="sm" fw={600} c="dimmed">
              {status.completed}/{status.total}
            </Text>
            <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
              Responses
            </Text>
          </Stack>
          <Group gap={4} className="med-survey-buttons">
            <Button
              size="xs"
              variant="light"
              color="violet"
              disabled={status.completed === 0}
              onClick={(e) => {
                e.stopPropagation();
                onReview();
              }}
            >
              Participants
            </Button>
            {onReviewCoaches && (
              <Button
                size="xs"
                variant="light"
                color="violet"
                onClick={(e) => {
                  e.stopPropagation();
                  onReviewCoaches();
                }}
              >
                {coachButtonLabel}
              </Button>
            )}
          </Group>
        </Group>
      </Group>
    );
  }

  if (status.state === 'future') {
    return (
      <Group
        justify="space-between"
        align="center"
        wrap="nowrap"
        style={{
          opacity: 0.7,
          padding: '16px 20px',
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        }}
        className="med-survey-line"
      >
        <Group gap="sm" align="center">
          <FileText size={14} color="var(--mantine-color-dimmed)" />
          <Text size="sm" fw={600} c="dimmed">
            {label}
          </Text>
          {status.scheduled_for ? (
            <Text size="xs" c="dimmed">
              Scheduled for {formatDate(status.scheduled_for)}
            </Text>
          ) : (
            <Text size="xs" c="dimmed">
              Not Started
            </Text>
          )}
        </Group>
      </Group>
    );
  }

  // state === 'complete'
  return (
    <Group
      justify="space-between"
      align="center"
      wrap="nowrap"
      style={{
        backgroundColor: '#F0F7F2',
        borderLeft: '3px solid #4A7C59',
        padding: '16px 20px',
        borderBottom: '1px solid var(--mantine-color-gray-2)',
      }}
      className="med-survey-line"
    >
      <Group gap="sm" align="center" className="med-survey-left">
        <FileText size={14} color="#4A7C59" />
        <Text size="sm" fw={600}>
          {label}
        </Text>
        <Badge size="sm" color="green" variant="light">
          Complete
        </Badge>
      </Group>
      <Group gap="md" align="center" className="med-survey-right">
        {status.avg_satisfaction !== null && (
          <Stack gap={0} align="center" className="med-survey-avg">
            <Text size="sm" fw={600} c="#4A7C59">
              {status.avg_satisfaction.toFixed(1)}
            </Text>
            <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
              Avg Satisfaction
            </Text>
          </Stack>
        )}
        <Stack gap={0} align="center" className="med-survey-responses">
          <Text size="sm" fw={600} c="dimmed">
            {status.completed}/{status.total}
          </Text>
          <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
            Responses
          </Text>
        </Stack>
        <Group gap={4} className="med-survey-buttons">
          <Button
            size="xs"
            variant="light"
            color="violet"
            onClick={(e) => {
              e.stopPropagation();
              onReview();
            }}
          >
            Participants
          </Button>
          {onReviewCoaches && (
            <Button
              size="xs"
              variant="light"
              color="violet"
              onClick={(e) => {
                e.stopPropagation();
                onReviewCoaches();
              }}
            >
              Coaches
            </Button>
          )}
        </Group>
      </Group>
    </Group>
  );
}

export default SurveyLineItem;
